import { useState } from 'react';
import {
  playbookCreate,
  playbookDelete,
  playbooks as playbooksRoute,
  playbookTestFire,
  playbookToggle,
  type Playbook,
  type PlaybookAction,
  type PlaybookCondition,
  type PlaybookTestFire,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding } from '../../session/DemoScope';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { ErrorNote } from '../../ErrorNote';
import { Skeleton } from '../../ui/states';
import { agoShort, msShort, parseDuration, pre, switchStyles, sx, useApi, type PolicyRole } from './util';
import { BuildLabel } from '../../ui/buildLabels';

const MONO = 'font-variant-numeric:tabular-nums;';
const FIELDS: PlaybookCondition['type'][] = ['gate_result', 'risk_score', 'velocity_breach', 'pattern_match', 'guard_state'];
const OPS: PlaybookCondition['operator'][] = ['==', '>', '<', '>=', '<='];
const ACTS: PlaybookAction['type'][] = ['kill_switch', 'response_level', 'notify', 'block_actor', 'escalate'];

/**
 * A stored playbook can arrive without a condition or actions (the server does not check what
 * POST /api/playbooks is given), so every reader below tolerates a missing one instead of
 * taking the whole Policy page down.
 */
const NO_CONDITION = 'no condition set';
export const condText = (c: PlaybookCondition | undefined) => (c ? `${c.type} ${c.operator} ${c.value}` : NO_CONDITION);
/** The IF sentence reads "exceeds" for > (design PB rows); the other operators stay as written. */
const condProse = (c: PlaybookCondition | undefined) => (c ? `${c.type} ${c.operator === '>' ? 'exceeds' : c.operator} ${c.value}` : NO_CONDITION);
export const actionText = (a: PlaybookAction) => {
  const vals = Object.values(a.params ?? {}).filter((v) => v !== null && v !== undefined && v !== '').map(String);
  return a.type + (vals.length ? '(' + vals.join(' · ') + ')' : '');
};
const windowed = (c: PlaybookCondition | undefined): c is PlaybookCondition => !!(c?.countThreshold && c?.windowMs);

/** Playbooks (design pcIsPlaybooks, T12878): if X, do Y, from the live playbook engine. */
export function PlaybooksTab({ pr }: { pr: PolicyRole }) {
  const { client } = useSession();
  const demoBuilding = useDemoBuilding();
  const roReason = useReadOnlyReason();
  const canWrite = pr === 'operator' && roReason === null;
  const writeTitle = roReason ?? (pr === 'operator' ? undefined : 'Playbooks are changed by Aithora.');
  const q = useApi((c, signal) => playbooksRoute(c, { signal }), []);
  const [filter, setFilter] = useState<'ALL' | 'ENABLED' | 'DISABLED'>('ALL');
  const [selId, setSelId] = useState<string | null>(null);
  const [toggle, setToggle] = useState<Playbook | null>(null);
  const [del, setDel] = useState<Playbook | null>(null);
  const [test, setTest] = useState<{ id: string; r: PlaybookTestFire } | null>(null);
  const [testErr, setTestErr] = useState<unknown>(null);
  const [modal, setModal] = useState(false);

  const all = q.data?.playbooks ?? [];
  const history = q.data?.history ?? [];
  const rows = all.filter((p) => filter === 'ALL' || (filter === 'ENABLED') === p.enabled);
  const sel = all.find((p) => p.id === selId) ?? all[0] ?? null;
  const enabledN = all.filter((p) => p.enabled).length;
  const firesTotal = all.reduce((n, p) => n + (p.fireCount ?? 0), 0);
  const lastFired = all.filter((p) => p.lastFiredAt).sort((a, b) => (b.lastFiredAt ?? 0) - (a.lastFiredAt ?? 0))[0];
  const frozen = q.data?.stats?.sessionsFrozen ?? history.filter((h) => h.actionsExecuted.some((a) => a.startsWith('kill_switch'))).length;
  const escalations = q.data?.stats?.escalations ?? history.filter((h) => h.actionsExecuted.some((a) => a.startsWith('response_level') || a.startsWith('escalate'))).length;
  const tiles = [
    { label: 'playbooks', fig: String(all.length), note: enabledN + ' enabled · ' + (all.length - enabledN) + ' off' },
    { label: 'fires', fig: String(firesTotal), note: 'auto-executed' },
    lastFired ? { label: 'last fired', fig: agoShort(new Date(lastFired.lastFiredAt!).toISOString()) + ' ago', note: (lastFired.name ?? '').toLowerCase() } : { label: 'Last fired', fig: 'never', note: 'no playbook has fired' },
    { label: 'sessions frozen', fig: String(frozen), note: 'via kill switch' },
    { label: 'escalations', fig: String(escalations), note: 'response-level raised' },
  ];

  const selHist = sel ? history.filter((h) => h.playbookId === sel.id).sort((a, b) => b.firedAt.localeCompare(a.firedAt)) : [];
  const sw = (on: boolean) => switchStyles(on);

  return (
    <div data-testid="policy-playbooks">
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
        {(['ALL', 'ENABLED', 'DISABLED'] as const).map((n) => (
          <a key={n} href="#" aria-pressed={filter === n} onClick={(e) => { pre(e); setFilter(n); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;white-space:nowrap;' + MONO + 'font-size:14px;font-weight:700;letter-spacing:.09em;text-decoration:none;flex:0 0 auto;' + (filter === n ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.14);'))}>
            {n}
          </a>
        ))}
        <span style={sx('display:flex;align-items:baseline;gap:4px 16px;flex-wrap:wrap;margin-left:8px;')}>
          {q.data &&
            tiles.map((t) => (
              <span key={t.label} title={t.note} style={sx('display:inline-flex;align-items:baseline;gap:5px;white-space:nowrap;')}>
                <span style={sx('font-size:18px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:#22272E;')}>{t.fig}</span>
                <span style={sx('font-size:16px;color:#3C454F;')}>{t.label}</span>
              </span>
            ))}
          {q.data?.real === false && !demoBuilding && <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;')}>SAMPLE</span></BuildLabel>}
        </span>
        <span style={sx('flex:1;')} />
        {pr !== 'readonly' && <a href="#" aria-disabled={!canWrite || undefined} title={writeTitle} onClick={(e) => { pre(e); if (canWrite) setModal(true); }} style={sx('display:inline-flex;align-items:center;gap:8px;height:26px;padding:0 15px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;')}>
          + New Playbook
        </a>}
      </div>

      {q.loading && !q.data && <Skeleton rows={5} label="Loading playbooks" />}
      {q.error && !q.data && <ErrorNote error={q.error} onRetry={q.reload} />}
      {q.data && (
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:14px;align-items:start;')}>
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:0;overflow:hidden;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;padding:13px 16px;border-bottom:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
              <span style={sx('font-size:16px;font-weight:700;')}>Response Rules</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;color:#54606C;')}>
                if X {'→'} do Y {'·'} <span>{rows.length}</span> shown
              </span>
            </div>
            {rows.map((p) => {
              const s3 = sw(p.enabled);
              const rest = (windowed(p.condition) ? '· ' + p.condition.countThreshold + '× in ' + msShort(p.condition.windowMs) + ' ' : '') + '→ ' + (p.actions ?? []).map(actionText).join(' · ');
              return (
                <div key={p.id} data-testid="playbook-row" style={sx('display:flex;align-items:center;gap:16px;flex-wrap:wrap;row-gap:6px;padding:14px 16px;border-bottom:1px solid rgba(34,39,46,.08);' + (sel?.id === p.id ? 'background:rgba(79,138,214,.07);box-shadow:inset 3px 0 0 #2F5C97;' : '') + (p.enabled ? '' : 'opacity:.6;'))}>
                  <a href="#" onClick={(e) => { pre(e); setSelId(p.id); }} style={sx('min-width:min(100%,130px);flex:1 1 130px;text-decoration:none;color:inherit;')}>
                    <span style={sx('display:block;font-size:16px;font-weight:700;')}>{p.name}</span>
                    <span style={sx('display:block;font-size:16px;margin-top:5px;')}>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>IF </span>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#3C454F;font-weight:600;')}>{condText(p.condition)}</span>
                      <span style={sx('color:#54606C;')}> {rest}</span>
                    </span>
                  </a>
                  <span style={sx('flex:0 1 auto;min-width:0;text-align:right;')}>
                    <span style={sx('display:block;font-size:19px;font-weight:700;color:' + (p.fireCount > 0 ? '#22272E' : '#54606C') + ';')}>{p.fireCount}</span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;')}>fires</span>
                  </span>
                  <a href="#" role="switch" aria-checked={p.enabled} aria-label={p.name} aria-disabled={!canWrite || undefined} title={writeTitle} onClick={(e) => { pre(e); if (canWrite) setToggle(p); }} style={sx(s3.wrap)}>
                    <span style={sx(s3.knob)} />
                  </a>
                </div>
              );
            })}
            {rows.length === 0 && (
              <div style={sx('font-size:16px;color:#54606C;padding:34px 16px;text-align:center;')}>
                {all.length === 0 ? 'No playbooks yet. A playbook runs a response automatically when its condition fires.' : filter === 'DISABLED' ? 'Every playbook is enabled.' : 'No playbooks match this filter.'}
              </div>
            )}
          </div>

          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:0;overflow:hidden;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;padding:13px 16px;border-bottom:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
              <span style={sx('font-size:16px;font-weight:700;')}>Playbook Detail</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{sel?.name ?? ''}</span>
            </div>
            {!sel && <div style={sx('font-size:16px;color:#54606C;padding:34px 16px;text-align:center;')}>Pick a playbook to see its condition, actions and history.</div>}
            {sel && (
              <div style={sx('padding:14px 16px;')}>
                <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
                  <span style={sx('font-size:16px;font-weight:700;min-width:0;')}>
                    {sel.name} <span style={sx('color:#54606C;font-weight:400;')}>{'·'} {sel.enabled ? 'armed' : 'off'}</span>
                  </span>
                  <span style={sx('flex:1;')} />
                  <a href="#" role="switch" aria-checked={sel.enabled} aria-label={sel.name + ' armed'} aria-disabled={!canWrite || undefined} title={writeTitle} onClick={(e) => { pre(e); if (canWrite) setToggle(sel); }} style={sx(sw(sel.enabled).wrap)}>
                    <span style={sx(sw(sel.enabled).knob)} />
                  </a>
                </div>
                <div style={sx('border:1px solid rgba(34,39,46,.11);border-radius:10px;overflow:hidden;margin-bottom:12px;')}>
                  <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;padding:8px 12px;background:rgba(34,39,46,.04);border-bottom:1px solid rgba(34,39,46,.09);')}>
                    <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:#22272E;')}>IF</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>condition</span>
                  </div>
                  <div style={sx('padding:12px;font-size:16px;line-height:1.7;color:#22272E;')}>
                    {'When ' + condProse(sel.condition) + (windowed(sel.condition) ? ', observed ' + sel.condition.countThreshold + '× within ' + msShort(sel.condition.windowMs) + '.' : ', immediately.')}
                  </div>
                </div>
                <div style={sx('border:1px solid rgba(34,39,46,.11);border-radius:10px;overflow:hidden;margin-bottom:14px;')}>
                  <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;padding:8px 12px;background:rgba(34,39,46,.04);border-bottom:1px solid rgba(34,39,46,.09);')}>
                    <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:#22272E;')}>THEN</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>actions</span>
                  </div>
                  <div style={sx('padding:12px;display:flex;flex-wrap:wrap;gap:8px;')}>
                    {(sel.actions ?? []).map((a, i) => (
                      <span key={i} style={sx('display:inline-flex;align-items:center;gap:7px;' + MONO + 'font-size:14px;font-weight:600;color:#54606C;padding:5px 10px;')}>
                        {actionText(a)}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>Stats</div>
                <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));border:1px solid rgba(34,39,46,.11);border-radius:10px;overflow:hidden;margin-bottom:14px;')}>
                  {[
                    ['FIRES TOTAL', String(sel.fireCount)],
                    ['COOLDOWN', msShort(sel.cooldownMs)],
                    ['LAST FIRED', sel.lastFiredAt ? agoShort(new Date(sel.lastFiredAt).toISOString()) + ' ago' : 'never'],
                  ].map(([k, v], i) => (
                    <div key={k} style={sx('padding:12px;text-align:center;' + (i < 2 ? 'border-right:1px solid rgba(34,39,46,.09);' : ''))}>
                      <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{v}</div>
                      <div style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;margin-top:4px;')}>{k}</div>
                    </div>
                  ))}
                </div>
                <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>Fire history</div>
                {selHist.map((h, i) => (
                  <div key={i} style={sx('display:flex;gap:12px;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                    <span style={sx('flex:0 0 34px;font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>{agoShort(h.firedAt)}</span>
                    <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                      <span style={sx('display:block;font-size:16px;color:#22272E;')}>{h.condition}</span>
                      <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:14px;color:#1F6B52;margin-top:3px;')}>{'→ ' + h.actionsExecuted.join(' · ')}</span>
                    </span>
                  </div>
                ))}
                {selHist.length === 0 && (
                  <div style={sx('font-size:16px;color:#54606C;padding:12px 0;line-height:1.55;')}>This rule has never fired, so there is no history to show. Its condition has not been met since it was armed.</div>
                )}
                {testErr != null && <ErrorNote error={testErr as never} compact />}
                <div style={sx('display:flex;gap:11px;margin-top:16px;')}>
                  <a
                    href="#"
                    aria-disabled={roReason !== null || undefined}
                    title={roReason ?? (test && test.id === sel.id ? 'Dry run: when it trips it would run ' + test.r.wouldExecute.map(actionText).join(', ') + '. Nothing was executed.' : 'A dry run: shows what would run, executes nothing.')}
                    onClick={async (e) => {
                      pre(e);
                      if (roReason) return;
                      setTestErr(null);
                      try {
                        const r = await playbookTestFire(client, { params: { id: sel.id }, body: {} });
                        setTest({ id: sel.id, r });
                      } catch (err) {
                        setTestErr(err);
                      }
                    }}
                    style={sx('flex:1;display:inline-flex;align-items:center;justify-content:center;height:38px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;')}
                  >
                    {test && test.id === sel.id ? 'Test queued' : 'Test fire'}
                  </a>
                  {pr !== 'readonly' && <a href="#" aria-disabled={!canWrite || undefined} title={writeTitle} onClick={(e) => { pre(e); if (canWrite) setDel(sel); }} style={sx('flex:1;display:inline-flex;align-items:center;justify-content:center;height:38px;border:1px solid rgba(179,38,30,.4);border-radius:9px;background:#FCFAF4;color:#B3261E;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;')}>
                    Delete
                  </a>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {toggle && (
        <ConfirmDialog
          title={toggle.enabled ? `Switch off "${toggle.name}"?` : `Switch on "${toggle.name}"?`}
          confirmLabel={toggle.enabled ? 'Switch off' : 'Switch on'}
          danger={toggle.enabled}
          body={<p>{toggle.enabled ? 'It stops running its response when the condition fires. The change is written to the Truth Ledger.' : 'It runs its response automatically the next time the condition fires.'}</p>}
          onConfirm={async () => {
            await playbookToggle(client, { params: { id: toggle.id }, body: withConfirm({}) });
            q.reload();
          }}
          onClose={() => setToggle(null)}
        />
      )}
      {del && (
        <ConfirmDialog
          title={`Delete "${del.name}"?`}
          confirmLabel="Delete"
          danger
          body={<p>It stops running and is removed. Its past fires stay in the Truth Ledger.</p>}
          onConfirm={async () => {
            await playbookDelete(client, { params: { id: del.id } });
            setSelId(null);
            q.reload();
          }}
          onClose={() => setDel(null)}
        />
      )}
      {modal && <NewPlaybook onClose={() => setModal(false)} onCreated={(id) => { setSelId(id); q.reload(); }} />}
    </div>
  );
}

/** "New playbook" (design pbModal, T12990). */
function NewPlaybook({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { client } = useSession();
  const [name, setName] = useState('');
  const [field, setField] = useState(0);
  const [op, setOp] = useState(0);
  const [value, setValue] = useState('');
  const [count, setCount] = useState('');
  const [win, setWin] = useState('');
  const [acts, setActs] = useState<string[]>([]);
  const [cooldown, setCooldown] = useState('5m');
  const [err, setErr] = useState<string | null>(null);
  const [apiErr, setApiErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const ready = name.trim() && value.trim() && acts.length > 0;
  const input = 'width:100%;height:38px;padding:0 12px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;';
  const label = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
  const create = async () => {
    if (!ready) {
      setErr('A playbook needs a name, a condition value and at least one action.');
      return;
    }
    const n = Number(value);
    const cnt = Number(count);
    const windowMs = win.trim() ? parseDuration(win) : null;
    const cooldownMs = parseDuration(cooldown);
    if ((win.trim() && windowMs === null) || cooldownMs === null) {
      setErr('Write the window and cooldown as a number and a unit, like 5m or 1h.');
      return;
    }
    setBusy(true);
    setApiErr(null);
    try {
      const r = await playbookCreate(client, {
        body: withConfirm({
          name: name.trim(),
          condition: {
            type: FIELDS[field],
            operator: OPS[op],
            value: value.trim() !== '' && Number.isFinite(n) ? n : value.trim(),
            ...(count.trim() && Number.isFinite(cnt) && windowMs ? { countThreshold: cnt, windowMs } : {}),
          },
          actions: acts.map((type) => ({ type, params: {} })),
          cooldownMs,
        }),
      });
      onCreated(r.playbook.id);
      onClose();
    } catch (e) {
      setApiErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="New playbook" style={sx('position:fixed;inset:0;z-index:60;background:rgba(34,39,46,.42);display:flex;align-items:center;justify-content:center;')}>
      <div style={sx('width:460px;max-width:92vw;max-height:88vh;overflow:auto;background:#FCFAF4;border-radius:13px;box-shadow:0 22px 54px rgba(34,39,46,.28);')}>
        <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;padding:16px 20px;border-bottom:1px solid rgba(34,39,46,.11);')}>
          <span style={sx('font-size:19px;font-weight:700;')}>New playbook</span>
          <span style={sx('flex:1;')} />
          <a href="#" aria-label="Close" onClick={(e) => { pre(e); onClose(); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.16);color:#54606C;text-decoration:none;font-size:16px;white-space:nowrap;')}>
            {'×'}
          </a>
        </div>
        <div style={sx('padding:16px 20px;')}>
          <div style={sx(label + 'margin-bottom:7px;')}>Name</div>
          <input aria-label="Name" value={name} onChange={(e) => { setName(e.target.value); setErr(null); }} placeholder="e.g. Freeze on feed loss" style={sx('width:100%;height:40px;padding:0 12px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;margin-bottom:15px;')} />
          <div style={sx(label + 'margin-bottom:7px;')}>If {'·'} condition</div>
          <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:11px;margin-bottom:11px;')}>
            <span style={sx('display:flex;align-items:center;height:38px;padding:0 12px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;')}>
              {FIELDS[field]}
              <span style={sx('flex:1;')} />
              <a href="#" aria-label="Next field" onClick={(e) => { pre(e); setField((field + 1) % FIELDS.length); }} style={sx('color:#54606C;text-decoration:none;')}>{'▾'}</a>
            </span>
            <span style={sx('display:flex;align-items:center;height:38px;padding:0 12px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;')}>
              {OPS[op]}
              <span style={sx('flex:1;')} />
              <a href="#" aria-label="Next operator" onClick={(e) => { pre(e); setOp((op + 1) % OPS.length); }} style={sx('color:#54606C;text-decoration:none;')}>{'▾'}</a>
            </span>
          </div>
          <input aria-label="Value" value={value} onChange={(e) => { setValue(e.target.value); setErr(null); }} placeholder={'value · e.g. BLOCK, 80, DEGRADED'} style={sx(input + 'margin-bottom:15px;')} />
          <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:11px;margin-bottom:15px;')}>
            <span>
              <span style={sx('display:block;' + label + 'margin-bottom:7px;')}>Count</span>
              <input aria-label="Count" value={count} onChange={(e) => setCount(e.target.value)} placeholder="e.g. 3" style={sx(input)} />
            </span>
            <span>
              <span style={sx('display:block;' + label + 'margin-bottom:7px;')}>Window</span>
              <input aria-label="Window" value={win} onChange={(e) => setWin(e.target.value)} placeholder="e.g. 5m" style={sx(input)} />
            </span>
          </div>
          <div style={sx(label + 'margin-bottom:9px;')}>Then {'·'} actions</div>
          <div style={sx('display:flex;flex-wrap:wrap;row-gap:6px;gap:9px;margin-bottom:15px;')}>
            {ACTS.map((n) => {
              const on = acts.includes(n);
              return (
                <a key={n} href="#" aria-pressed={on} onClick={(e) => { pre(e); setActs(on ? acts.filter((x) => x !== n) : [...acts, n]); setErr(null); }} style={sx('display:inline-flex;align-items:center;gap:8px;height:32px;padding:0 12px;border-radius:8px;font-size:16px;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (on ? 'background:rgba(79,138,214,.12);color:#2F5C97;border:1px solid rgba(47,92,151,.4);' : 'background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.14);'))}>
                  <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:15px;height:22px;flex:0 0 15px;border-radius:4px;font-size:14px;font-weight:700;white-space:nowrap;' + (on ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.24);color:transparent;'))}>{on ? '✓' : ''}</span>
                  {n}
                </a>
              );
            })}
          </div>
          <div style={sx(label + 'margin-bottom:7px;')}>Cooldown</div>
          <input aria-label="Cooldown" value={cooldown} onChange={(e) => setCooldown(e.target.value)} style={sx(input)} />
          {err && <div role="alert" style={sx('font-size:16px;color:#B3261E;margin-top:11px;')}>{err}</div>}
          {apiErr != null && <ErrorNote error={apiErr as never} compact />}
        </div>
        <div style={sx('display:flex;gap:11px;padding:0 20px 20px;')}>
          <a href="#" onClick={(e) => { pre(e); onClose(); }} style={sx('flex:1;display:inline-flex;align-items:center;justify-content:center;height:42px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;')}>
            Cancel
          </a>
          <a href="#" aria-disabled={busy || undefined} onClick={(e) => { pre(e); if (!busy) void create(); }} style={sx('flex:1;display:inline-flex;align-items:center;justify-content:center;height:42px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;' + (ready ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);'))}>
            {busy ? 'Creating…' : 'Create playbook'}
          </a>
        </div>
      </div>
    </div>
  );
}
